import React, { MutableRefObject, ReactNode, useEffect, useId, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { graphql, useFragment, useMutation } from "react-relay";
import type { Disposable } from "relay-runtime";
import { keyframes } from "@emotion/react";
import { Controller, FormProvider, useController, useForm } from "react-hook-form";
import { LuCircleCheck, LuUpload, LuInfo } from "react-icons/lu";
import {
    Spinner, WithTooltip, assertNever, bug,
    unreachable, Button, boxError, ErrorBox, Card,
} from "@opencast/appkit";

import { RootLoader } from "../layout/Root";
import { fetchQuery, loadQuery } from "../relay";
import { UploadQuery, UploadQuery$data } from "./__generated__/UploadQuery.graphql";
import { makeRoute } from "../rauta";
import { ErrorDisplay, errorDisplayInfo } from "../util/err";
import { aclArrayToMap, aclMapToArray, useNavBlocker } from "./util";
import CONFIG from "../config";
import { LinkButton } from "../ui/LinkButton";
import { isRealUser, User, useUser } from "../User";
import { currentRef, Inertable, keyOfId, seriesId, useRefState } from "../util/index";
import {
    FieldIsRequiredNote,
    InputContainer,
    MetadataFields,
    MetadataForm,
} from "../ui/metadata";
import { PageTitle } from "../layout/header/ui";
import { Link, useRouter } from "../router";
import { getJwt } from "../relay/auth";
import { VideoListSelector } from "../ui/SearchableSelect";
import { Breadcrumbs } from "../ui/Breadcrumbs";
import { ManageNav, ManageRoute } from "./manage";
import { COLORS } from "../color";
import { COMMON_ROLES, defaultAclMap } from "../util/roles";
import { Acl, AclSelector, knownRolesFragment } from "../ui/Access";
import {
    AccessKnownRolesData$data,
    AccessKnownRolesData$key,
} from "../ui/__generated__/AccessKnownRolesData.graphql";
import { READ_WRITE_ACTIONS } from "../util/permissionLevels";
import {
    UploadSeriesAclQuery,
    UploadSeriesAclQuery$data,
} from "./__generated__/UploadSeriesAclQuery.graphql";
import {
    NewEvent,
    UploadCreatePlaceholderMutation,
} from "./__generated__/UploadCreatePlaceholderMutation.graphql";
import { UploadCreateSeriesMutation } from "./__generated__/UploadCreateSeriesMutation.graphql";
import { ManageVideosRoute } from "./manage/Video";
import { CreateVideoList } from "./manage/Shared/Create";
import { CreateSeriesPageProps } from "./manage/Series/Create";


export const PATH = "/~manage/upload" as const;
export const UploadRoute = makeRoute({
    url: ({ seriesId }: { seriesId?: string | null } = {}): `${typeof PATH}${string | null}` =>
        seriesId ? `${PATH}?${new URLSearchParams({ series: seriesId })}` : PATH,
    match: url => {
        if (url.pathname !== PATH) {
            return null;
        }

        const seriesParam = url.searchParams.get("series");
        const preselectedSeries = seriesParam && seriesId(seriesParam);

        const queryRef = loadQuery<UploadQuery>(query, { seriesId: preselectedSeries ?? "" });
        return {
            render: () => <RootLoader
                {...{ query, queryRef }}
                noindex
                nav={() => <ManageNav active={PATH} />}
                render={data => <Upload uploadQueryData={data} seriesUrlParamSet={!!seriesParam} />}
            />,
            dispose: () => queryRef.dispose(),
        };
    },
});

const query = graphql`
    query UploadQuery($seriesId: ID!) {
        ... UserData
        ... AccessKnownRolesData
        writableSeries: searchAllSeries(query: "", writableOnly: true) {
            ... on SeriesSearchResults {
                items { id }
            }
        }
        series: seriesById(id: $seriesId) {
            id
            opencastId
            title
            description
            created
            canWrite
            acl { role actions info { label implies safeActions assignableActions } }
        }
    }
`;

export type AclArray = NonNullable<UploadSeriesAclQuery$data["series"]>["acl"];
type Metadata = {
    title: string;
    description: string;
    series?: {
        id: string;
        ocId: string;
        acl: AclArray;
    };
    acl: Acl;
    thumbnail?: File | null;
};

type Props = {
    uploadQueryData: UploadQuery$data;
    seriesUrlParamSet: boolean;
};

const Upload: React.FC<Props> = ({ uploadQueryData, seriesUrlParamSet }) => {
    const { t } = useTranslation();
    const knownRoles = useFragment<AccessKnownRolesData$key>(knownRolesFragment, uploadQueryData);
    const preselectedSeries = uploadQueryData.series;
    const user = useUser();

    if (preselectedSeries && !preselectedSeries.canWrite) {
        return <Card kind="error">
            {t("upload.missing-series-write-permission")}
        </Card>;
    }

    const breadcrumbs = <Breadcrumbs
        path={[{ label: t("user.manage"), link: ManageRoute.url }]}
        tail={t("upload.title")}
    />;

    const userCanUpload = isRealUser(user) && user.canUpload;
    const noWritableSeries = CONFIG.behavior.disallowEventsWithoutSeries
        && (uploadQueryData.writableSeries.items?.length ?? 0) === 0
        && !preselectedSeries;

    if (noWritableSeries && userCanUpload) {
        return <>
            {breadcrumbs}
            <PageTitle title={t("upload.title")} />
            {user.canCreateSeries
                ? <SeriesCreationStep knownRolesRef={uploadQueryData} />
                : <Card kind="error">{t("upload.no-writable-series-blocked")}</Card>
            }
        </>;
    }

    return (
        <div css={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
        }}>
            {breadcrumbs}
            <PageTitle title={t("upload.title")} />
            {seriesUrlParamSet && preselectedSeries == null && (
                <Card kind="error" css={{ marginBottom: 16 }}>
                    {t("upload.specified-series-not-found")}
                </Card>
            )}
            <UploadMain {...{ knownRoles, preselectedSeries }} />
        </div>
    );
};


type CancelButtonProps = {
    abortController: MutableRefObject<AbortController>;
};

const CancelButton: React.FC<CancelButtonProps> = ({ abortController }) => {
    const { t } = useTranslation();

    return (
        <div css={{
            margin: "8px",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
        }}>
            <Button
                kind="danger"
                onClick={() => abortController.current.abort()}
            >{t("upload.cancel")}</Button>
        </div>
    );
};

const createPlaceholderMutation = graphql`
    mutation UploadCreatePlaceholderMutation($event: NewEvent!) {
        createPlaceholderEvent(event: $event) { id }
    }
`;

const createSeriesMutation = graphql`
    mutation UploadCreateSeriesMutation(
        $metadata: BasicMetadata!,
        $acl: [AclItem!]!,
        $creator: String!,
    ) {
        createSeries(metadata: $metadata, acl: $acl, creator: $creator) { id }
    }
`;

type UploadMainProps = {
    knownRoles: AccessKnownRolesData$data;
    preselectedSeries?: UploadQuery$data["series"];
};

const UploadMain: React.FC<UploadMainProps> = ({ knownRoles, preselectedSeries }) => {
    const { t } = useTranslation();
    const router = useRouter();

    const [files, setFiles] = useState<FileList | null>(null);
    const [uploadState, setUploadState] = useRefState<UploadState | null>(null);
    const [metadata, setMetadata] = useRefState<Metadata | null>(null);
    const selectedFileName = (
        <div css={{ fontSize: 14, color: COLORS.neutral60, alignSelf: "flex-start" }}>
            {files?.[0]?.name}
        </div>
    );

    const progressHistory = useRef<ProgressHistory>([]);
    const abortController = useRef(new AbortController());
    const [commit] = useMutation<UploadCreatePlaceholderMutation>(createPlaceholderMutation);

    useEffect(() => {
        checkInfoMe().catch(error => setUploadState({ state: "error", error }));
    }, []);

    const createPlaceholder = async (event: NewEvent) => commit({
        variables: {
            event: {
                ...event,
                acl: event.acl,
            },
        },
        updater: store => store. invalidateStore(),
    });

    router.listenAtNav(() => {
        const state = uploadState.current?.state;
        if (state && !["done", "cancelled", "error"].includes(state)) {
            abortController.current.abort();
        }
    });

    useNavBlocker(() => !!uploadState.current
        && !["done", "cancelled"].includes(uploadState.current.state));

    // Get user info
    const user = useUser();
    if (!isRealUser(user) || !user.canUpload) {
        // TODO: if not logged in, suggest doing so
        return <div css={{ textAlign: "center" }}>
            <ErrorBox>{t("upload.not-authorized")}</ErrorBox>
        </div>;
    }

    /** Called when the files are selected. Starts uploading those files. */
    const onFileSelect = async (files: FileList) => {
        setFiles(files);

        const onProgressCallback = (progress: Progress): void => {
            if (uploadState.current === null) {
                return unreachable("no upload state after calling `startUpload`");
            }
            onProgress(
                progress,
                progressHistory.current,
                uploadState.current,
                setUploadState,
                abortController,
            );
        };
        const onDone = (mediaPackage: string) => {
            if (metadata.current === null) {
                setUploadState({ state: "waiting-for-metadata", mediaPackage, abortController });
            } else {
                finishUpload(
                    mediaPackage,
                    metadata.current,
                    user,
                    setUploadState,
                    createPlaceholder,
                );
            }
        };
        startUpload(files, setUploadState, onProgressCallback, onDone, abortController);
    };

    if (files === null) {
        return <>
            {uploadState.current?.state === "error" && <>
                <UploadErrorBox error={uploadState.current.error} />
                <div css={{ height: 16 }} />
            </>}
            <FileSelect onSelect={onFileSelect} />
        </>;
    } else if (uploadState.current === null) {
        // This never happens as, when files are selected, the upload is
        // instantly started and the state is set to `starting`. Check the only
        // use of `setFiles` above and notice how the upload state is set
        // immediately afterwards.
        return unreachable("upload state is null, but there are files");
    } else if (uploadState.current.state === "done") {
        return <div css={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "max(16px, 10vh - 50px)",
            gap: 32,
        }}>
            {selectedFileName}
            <LuCircleCheck css={{ fontSize: 64, color: COLORS.happy0 }} />
            <p>
                <Trans i18nKey="upload.finished">
                    It can be found on the <Link to={ManageVideosRoute.url} /> page.
                </Trans>
            </p>
            <LinkButton kind="call-to-action" to={UploadRoute.url({
                seriesId: preselectedSeries ? keyOfId(preselectedSeries.id) : null,
            })}>
                {t("upload.upload-another")}
            </LinkButton>
        </div>;
    } else {
        const onMetadataSave = (metadata: Metadata): void => {
            if (uploadState.current === null) {
                return bug("uploadState === null on metadata save");
            }

            setMetadata(metadata);
            if (uploadState.current.state === "waiting-for-metadata") {
                // The tracks have already been uploaded, so we can finish the upload now.
                const mediaPackage = uploadState.current.mediaPackage;
                finishUpload(mediaPackage, metadata, user, setUploadState, createPlaceholder);
            }
        };
        const hasUploadError = uploadState.current.state === "error";

        return (
            <div css={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 32,
                width: "100%",
                maxWidth: 900,
            }}>
                {selectedFileName}
                <UploadState
                    state={uploadState.current}
                    seriesId={preselectedSeries?.id}
                />
                <div>
                    {/* TODO: Show something after saving metadata.
                        - Just saying "saved" is kind of misleading because the data is only local.
                        - Maybe just show the form, but disable all inputs?
                        - ...
                    */}
                    {uploadState.current.state !== "cancelled" && (
                        !metadata.current
                            ? <MetaDataEdit
                                onSave={onMetadataSave}
                                disabled={hasUploadError}
                                {...{ knownRoles, preselectedSeries }}
                            />
                            : !hasUploadError && (
                                <div css={{ margin: "0 auto", maxWidth: 500 }}>
                                    <Card kind="info">{t("upload.still-uploading")}</Card>
                                </div>
                            )
                    )}
                </div>
            </div>
        );
    }
};

const SeriesCreationStep: React.FC<CreateSeriesPageProps> = ({ knownRolesRef }) => {
    const { t } = useTranslation();
    const [commit, inFlight] = useMutation<UploadCreateSeriesMutation>(createSeriesMutation);

    return <div css={{ maxWidth: 900 }}>
        <Card kind="info" css={{ marginBottom: 20 }}>
            {t("upload.create-series-first")}
        </Card>
        <CreateVideoList
            {...{ commit, inFlight, knownRolesRef }}
            canUserCreateList={user => user.canCreateSeries}
            kind="series"
            buildVariables={({ displayName }) => ({
                creator: displayName,
            })}
            returnPath={response => UploadRoute.url({
                seriesId: keyOfId(response.createSeries.id),
            })}
            showTitle={false}
        />
    </div>;
};

type ProgressHistory = {
    timestamp: number;
    progress: Progress;
}[];

/**
 * Called regularly with the current progress and calculates the time estimate.
 * This is done with a simple sliding average over the last few data points,
 * that is assumed to be the speed for the rest of the upload.
 */
const onProgress = (
    progress: Progress,
    history: ProgressHistory,
    uploadState: UploadState,
    setUploadState: (state: UploadState) => void,
    abortController: MutableRefObject<AbortController>,
) => {
    const now = Date.now();

    // Add progress data point to history.
    history.push({ timestamp: now, progress });

    // The size of the sliding window in milliseconds.
    const WINDOW_SIZE_MS = 5000;
    // The size of the sliding window in number of data points.
    const WINDOW_SIZE_DATA_POINTS = 6;
    // The number of datapoints below which we won't show a time estimate.
    const MINIMUM_DATA_POINT_COUNT = 4;

    // Find the first element within the window. We use the larger window of the
    // two windows created by the two constraints (time and number of
    // datapoints).
    const windowStart = Math.min(
        history.findIndex(p => (now - p.timestamp) < WINDOW_SIZE_MS),
        Math.max(0, history.length - WINDOW_SIZE_DATA_POINTS),
    );

    // Remove all elements outside the window.
    history.splice(0, windowStart);

    let secondsLeft = null;
    if (history.length >= MINIMUM_DATA_POINT_COUNT) {
        // Calculate the remaining time based on the average speed within the window.
        const windowLength = now - history[0].timestamp;
        const progressInWindow = progress - history[0].progress;
        const progressPerSecond = (progressInWindow / windowLength) * 1000;
        const progressLeft = 1 - progress;
        secondsLeft = Math.max(0, Math.round(progressLeft / progressPerSecond));
    }

    // Update state if anything changed. We actually check for equality here to
    // avoid useless redraws.
    const someChange = uploadState.state !== "uploading-tracks"
        || uploadState.secondsLeft !== secondsLeft
        || uploadState.progress !== progress;
    if (someChange) {
        setUploadState({ state: "uploading-tracks", progress, secondsLeft, abortController });
    }
};

const UploadErrorBox: React.FC<{ error: unknown }> = ({ error }) => {
    // Log error once.
    // eslint-disable-next-line no-console
    useEffect(() => console.error("Error uploading: ", error), [error]);

    const { t, i18n } = useTranslation();
    let info;
    if (error instanceof OcNetworkError) {
        info = {
            causes: new Set([t("upload.errors.opencast-unreachable")]),
            // Opencast could be down, but it's confusing setting this to true
            probablyOurFault: false,
            potentiallyInternetProblem: true,
        };
    } else if (error instanceof OcServerError) {
        if (error.status === 413) { // Entity too large
            info = {
                causes: new Set([t("upload.errors.too-large")]),
                probablyOurFault: false,
                potentiallyInternetProblem: false,
            };
        } else {
            info = {
                causes: new Set([t("upload.errors.opencast-server-error")]),
                probablyOurFault: true,
                potentiallyInternetProblem: false,
            };
        }
    } else if (error instanceof JwtInvalid) {
        info = {
            causes: new Set([t("upload.errors.jwt-invalid")]),
            probablyOurFault: true,
            potentiallyInternetProblem: false,
        };
    } else if (error instanceof OcUnexpectedResponse) {
        info = {
            causes: new Set([t("errors.unexpected-response")]),
            probablyOurFault: true,
            potentiallyInternetProblem: false,
        };
    } else {
        info = errorDisplayInfo(error, i18n);
    }


    return (
        <div css={{ maxWidth: 750 }}>
            <Card kind="error">
                <ErrorDisplay info={info} failedAction={t("upload.errors.failed-to-upload")} />
            </Card>
        </div>
    );
};



// ==============================================================================================
// ===== Sub-components
// ==============================================================================================

const isValidForUpload = (file: File): boolean =>
    file.type.startsWith("video/") || file.type.startsWith("audio/");

type FileSelectProps = {
    onSelect: (files: FileList) => void;
};

/** First state of the uploader: asking the user to select video files */
const FileSelect: React.FC<FileSelectProps> = ({ onSelect }) => {
    const { t } = useTranslation();
    const fileInput = useRef<HTMLInputElement>(null);

    const [error, setError] = useState<string | null>(null);
    const [dragCounter, setDragCounter] = useState(0);
    const isDragging = dragCounter > 0;

    const onSelectRaw = (files: FileList) => {
        if (files.length === 0) {
            setError(t("upload.not-a-file"));
        } else if (files.length > 1) {
            setError(t("upload.too-many-files"));
        } else if (!Array.from(files).every(isValidForUpload)) {
            setError(t("upload.not-video-or-audio"));
        } else {
            onSelect(files);
        }
    };

    return (
        <div
            onDragEnter={e => {
                setDragCounter(old => old + 1);
                e.preventDefault();
            }}
            onDragOver={e => e.preventDefault()}
            onDragLeave={() => setDragCounter(old => old - 1)}
            onDrop={e => {
                onSelectRaw(e.dataTransfer.files);
                setDragCounter(0);
                e.preventDefault();
            }}
            css={{
                width: "100%",
                height: "100%",
                border: "2.5px dashed",
                borderRadius: 10,
                display: "flex",
                padding: 8,
                flexDirection: "column",
                gap: 16,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isDragging ? COLORS.neutral10 : "none",
                borderColor: isDragging ? COLORS.primary0 : COLORS.neutral25,
                "--transition-length": "80ms",
                transition: "background-color var(--transition-length), "
                    + "border-color var(--transition-length)",
            }}
        >
            {/* Big icon */}
            <div css={{
                position: "relative",
                lineHeight: 1,
                fontSize: 64,
                color: COLORS.neutral60,
            }}>
                {/* This depends on the SVG elements used in the icon. Technically, the icon pack
                    does not guarantee that and could change it at any time. But we decided it's
                    fine in this case. It is unlikely to change and if it breaks, nothing bad could
                    happen. Only the animation is broken. */}
                <LuUpload css={{
                    position: "absolute",
                    top: isDragging ? 8 : 0,
                    transition: "top var(--transition-length)",
                    "& > path": { display: "none" },
                }} />
                <LuUpload css={{ "& > polyline, & > line": { display: "none" } }} />
            </div>

            {t("upload.drop-to-upload")}

            {/* "Select files" button */}
            <div css={{ marginTop: 16 }}>
                <Button
                    kind="call-to-action"
                    onClick={() => currentRef(fileInput).click()}
                >{t("upload.select-files")}</Button>
                <input
                    ref={fileInput}
                    onChange={e => {
                        if (e.target.files) {
                            onSelectRaw(e.target.files);
                        }
                    }}
                    type="file"
                    accept="video/*, audio/*"
                    aria-hidden="true"
                    css={{ display: "none" }}
                />
            </div>

            {boxError(error)}
        </div>
    );
};

/** Number between 0 and 1 */
type Progress = number;

/** State of a started upload */
type UploadState =
    // Starting the upload: creating a new media package.
    { state: "starting" }
    // Uploading the actual tracks, usually takes by far the longest.
    | { state: "uploading-tracks"; progress: Progress; secondsLeft: number | null;
        abortController: MutableRefObject<AbortController>; }
    // Tracks have been uploaded, but the user still has to save the metadata to finish the upload.
    | { state: "waiting-for-metadata"; mediaPackage: string;
        abortController: MutableRefObject<AbortController>; }
    // After the tracks have been uploaded, just adding metadata, ACL and then ingesting.
    | { state: "finishing" }
    // The upload is completely done
    | { state: "done" }
    // The upload was cancelled
    | { state: "cancelled" }
    // An error occurred during the upload
    | { state: "error"; error: unknown };

/** State of an upload that is not yet finished */
type NonFinishedUploadState = Exclude<UploadState, { state: "done" }>;
type UploadStateProps = {
    state: NonFinishedUploadState;
    seriesId?: string | null;
};

/** Shows the current state of the upload */
const UploadState: React.FC<UploadStateProps> = ({ state, seriesId }) => {
    const { t, i18n } = useTranslation();

    if (state.state === "starting") {
        return <BarWithText state="progressing">
            <span>{t("upload.starting")}</span>
        </BarWithText>;
    } else if (state.state === "uploading-tracks") {
        const abortController = state.abortController;
        const progress = Math.min(1, state.progress);
        const roundedPercent = (progress * 100).toLocaleString(i18n.language, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        });

        // Nicely format the remaining time.
        const secondsLeft = state.secondsLeft;
        let prettyTime;
        if (secondsLeft === null) {
            prettyTime = null;
        } else if (secondsLeft < 4) {
            prettyTime = t("upload.time-estimate.a-few-seconds");
        } else if (secondsLeft < 45) {
            prettyTime = `${secondsLeft} ${t("general.seconds")}`;
        } else if (secondsLeft < 90) {
            prettyTime = t("upload.time-estimate.a-minute");
        } else if (secondsLeft < 45 * 60) {
            prettyTime = `${Math.round(secondsLeft / 60)} ${t("general.minutes")}`;
        } else if (secondsLeft < 90 * 60) {
            prettyTime = t("upload.time-estimate.an-hour");
        } else if (secondsLeft < 24 * 60 * 60) {
            const hours = Math.round(secondsLeft / (60 * 60));
            prettyTime = `${hours} ${t("general.hours")}`;
        } else {
            prettyTime = null;
        }

        return <>
            <BarWithText state={progress}>
                <span>{roundedPercent}%</span>
                <span>
                    {prettyTime && t("upload.time-estimate.time-left", { time: prettyTime })}
                </span>
            </BarWithText>
            <CancelButton abortController={abortController} />
        </>;
    } else if (state.state === "waiting-for-metadata") {
        const abortController = state.abortController;
        return <>
            <BarWithText state="waiting">
                <span>{t("upload.waiting-for-metadata")}</span>
            </BarWithText>
            <CancelButton abortController={abortController} />
        </>;
    } else if (state.state === "finishing") {
        return <BarWithText state="progressing">
            <span>{t("upload.finishing")}</span>
        </BarWithText>;
    } else if (state.state === "cancelled") {
        return <div css={{
            marginTop: "1rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            "& > :first-child": { marginBottom: "2rem" },
        }}>
            <span>{t("upload.upload-cancelled")}</span>
            <LinkButton kind="call-to-action" to={UploadRoute.url({
                seriesId: seriesId ? keyOfId(seriesId) : null,
            })}>
                {t("upload.select-files")}
            </LinkButton>
        </div>;
    } else if (state.state === "error") {
        return <UploadErrorBox error={state.error} />;
    } else {
        return assertNever(state);
    }
};

type BarWithTextProps = {
    state: ProgressBarProps["state"];
    children: ReactNode;
};

/** Helper component for `UploadState` */
const BarWithText: React.FC<BarWithTextProps> = ({ state, children }) => <>
    <div>
        <div css={{
            display: "flex",
            justifyContent: "space-between",
            padding: "0 4px",
            marginBottom: 8,
            "& > *:only-child": { margin: "0 auto" },
        }}>{children}</div>
        <ProgressBar state={state} />
    </div>
</>;

type ProgressBarProps = {
    /** Either a known progress, or an unknown progress or waiting for something */
    state: Progress | "progressing" | "waiting";
};

/** A progress bar that can show different states */
const ProgressBar: React.FC<ProgressBarProps> = ({ state }) => {
    // Helper function to create a moving stripe background.
    const animatedStripes = (
        angle: number,
        color0: string,
        color1: string,
        duration: number,
    ) => {
        const size = 30;
        const amountColor0 = 0.4;

        // The input size is the horizontal period basically. But the CSS
        // gradient expect the period in direction of the pattern, so we have
        // to do a little math.
        const realSize = size * Math.sin(Math.abs(angle) / 180 * Math.PI);

        return {
            background: "repeating-linear-gradient("
                + `${angle}deg,`
                + `${color0},`
                + `${color0} ${realSize * amountColor0}px,`
                + `${color1} ${realSize * amountColor0}px,`
                + `${color1} ${realSize}px)`,
            backgroundSize: `calc(100% + ${size}px) 100%`,
            animation: `${duration}s linear infinite none ${keyframes({
                "0%": { backgroundPositionX: -30 },
                "100%": { backgroundPositionX: 0 },
            })}`,
        };
    };

    const shared = {
        width: "100%",
        height: 12,
        borderRadius: 6,
        overflow: "hidden",
    };

    if (state === "progressing") {
        return <div css={{
            ...shared,
            ...animatedStripes(-45, COLORS.happy2, COLORS.happy0, 1.5),
        }} />;
    } else if (state === "waiting") {
        return <div css={{
            ...shared,
            ...animatedStripes(45, COLORS.neutral60, COLORS.neutral40, 4),
        }} />;
    } else {
        return (
            <div css={{
                ...shared,
                backgroundColor: COLORS.neutral15,
            }}>
                <div css={{
                    height: "100%",
                    transition: "width 200ms",
                    width: `${state * 100}%`,
                    backgroundColor: COLORS.happy0,
                }} />
            </div>
        );
    }
};


const SeriesAclQuery = graphql`
    query UploadSeriesAclQuery($seriesId: String!) {
        series: seriesByOpencastId(id: $seriesId) {
            id
            acl { role actions info { label implies safeActions assignableActions } }
        }
    }
`;

type MetaDataEditProps = {
    onSave: (metadata: Metadata) => void;
    disabled: boolean;
    knownRoles: AccessKnownRolesData$data;
    preselectedSeries?: UploadQuery$data["series"];
};

/** Form that lets the user set metadata about the video */
const MetaDataEdit: React.FC<MetaDataEditProps> = ({
    onSave,
    disabled,
    knownRoles,
    preselectedSeries,
}) => {
    const { t } = useTranslation();
    const u = useUser();
    const user = isRealUser(u) ? u : unreachable();

    const seriesFieldId = useId();
    const thumbnailInputRef = useRef<HTMLInputElement | null>(null);
    const [lockedAcl, setLockedAcl] = useState<Acl | null>(
        preselectedSeries ? aclArrayToMap(preselectedSeries.acl) : null,
    );
    const [aclError, setAclError] = useState<ReactNode>(null);
    const [aclLoading, setAclLoading] = useState(false);
    const lockToSeries = CONFIG.lockAclToSeries && lockedAcl;
    const aclEditingLocked = !!lockToSeries || aclLoading || !!aclError;

    const fetchSeriesAcl = async (seriesId: string): Promise<Acl | null> => {
        const data = await fetchQuery<UploadSeriesAclQuery>(
            SeriesAclQuery,
            { seriesId },
        ).toPromise();

        if (!data?.series?.acl) {
            return null;
        }

        return aclArrayToMap(data.series.acl);
    };

    const onSeriesChange = async (data: {
        opencastId?: string;
        id?: string;
    }) => {
        setAclError(null);

        if (!data?.opencastId) {
            setLockedAcl(null);
            seriesField.onChange(undefined);
            return;
        }

        seriesField.onChange({ id: data.id, ocId: data.opencastId });

        if (CONFIG.lockAclToSeries) {
            setAclLoading(true);
            try {
                const seriesAcl = await fetchSeriesAcl(data.opencastId);
                setLockedAcl(seriesAcl);
                seriesField.onChange({
                    id: data.id,
                    ocId: data.opencastId,
                    acl: seriesAcl,
                });
            } catch (e) {
                setAclError(
                    <ErrorDisplay
                        error={e}
                        failedAction={t("upload.errors.failed-fetching-series-acl")}
                    />,
                );
            } finally {
                setAclLoading(false);
            }
        }
    };

    const formMethods = useForm<Metadata>({
        mode: "onChange",
        defaultValues: { acl: defaultAclMap(user, knownRoles) },
    });
    const { handleSubmit, control, formState: { isValid, errors } } = formMethods;

    const { field: seriesField } = useController({
        name: "series",
        control,
        ...preselectedSeries && {
            defaultValue: {
                id: preselectedSeries.id,
                ocId: preselectedSeries.opencastId,
                acl: preselectedSeries.acl,
            },
        },
        rules: {
            required: CONFIG.behavior.disallowEventsWithoutSeries
                ? t("manage.metadata-form.errors.field-required")
                : false,
        },
    });

    const onSubmit = handleSubmit(data => onSave(data));


    return <FormProvider {...formMethods}>
        <MetadataForm>
            {/* Title & Description */}
            <MetadataFields />

            {/* Series */}
            <InputContainer css={{ maxWidth: 750 }}>
                <label htmlFor={seriesFieldId}>
                    {t("series.singular")}
                    {CONFIG.behavior.disallowEventsWithoutSeries && <FieldIsRequiredNote />}
                    <WithTooltip
                        tooltip={t("upload.metadata.note-writable-series")}
                        tooltipCss={{ width: 400 }}
                        css={{
                            display: "inline-block",
                            verticalAlign: "middle",
                            fontWeight: "normal",
                            marginLeft: 8,
                        }}
                    >
                        <span><LuInfo tabIndex={0} /></span>
                    </WithTooltip>
                </label>
                <Inertable isInert={!!preselectedSeries} >
                    <VideoListSelector
                        type="series"
                        inputId={seriesFieldId}
                        writableOnly
                        menuPlacement="top"
                        onChange={data => onSeriesChange({
                            opencastId: data?.opencastId,
                            id: data?.id,
                        })}
                        onBlur={seriesField.onBlur}
                        required={CONFIG.behavior.disallowEventsWithoutSeries}
                        defaultValue={preselectedSeries ?? undefined}
                    />
                </Inertable>
                {boxError(errors.series?.message)}
            </InputContainer>

            {/* Thumbnail */}
            <InputContainer css={{ maxWidth: 750 }}>
                <label>{t("upload.thumbnail.title")}</label>
                <Controller
                    name="thumbnail"
                    control={control}
                    render={({ field }) => (
                        <div css={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <input
                                ref={thumbnailInputRef}
                                type="file"
                                accept="image/*"
                                onChange={e => {
                                    const f = e.target.files?.[0] ?? null;
                                    field.onChange(f);
                                }}
                                onBlur={field.onBlur}
                                aria-hidden="true"
                                css={{ display: "none" }}
                            />

                            <Button onClick={() => thumbnailInputRef.current?.click()}>
                                {t("upload.thumbnail.upload")}
                            </Button>

                            <span css={{ fontSize: 14, color: COLORS.neutral60 }}>
                                {field.value?.name ?? <i>{t("upload.thumbnail.no-file")}</i>}
                            </span>
                        </div>
                    )}
                />
            </InputContainer>

            {/* ACL */}
            <InputContainer>
                <h2 css={{
                    marginTop: 32,
                    marginBottom: 12,
                    fontSize: 22,
                }}>{t("acl.title")}</h2>
                {boxError(aclError)}
                {aclLoading && <Spinner size={20} />}
                {lockToSeries && (
                    <Card kind="info" iconPos="left" css={{
                        maxWidth: 700,
                        fontSize: 14,
                        marginBottom: 10,
                    }}>
                        <Trans i18nKey="acl.locked-to-series">
                            <>series</>
                        </Trans>
                    </Card>
                )}
                <Inertable isInert={aclEditingLocked}>
                    <Controller
                        name="acl"
                        control={control}
                        render={({ field }) => <AclSelector
                            itemType="video"
                            userIsRequired
                            onChange={field.onChange}
                            acl={lockToSeries ? lockedAcl : field.value}
                            knownRoles={knownRoles}
                            permissionLevels={READ_WRITE_ACTIONS}
                        />}
                    />
                </Inertable>
            </InputContainer>

            {/* Submit */}
            <Button
                kind="call-to-action"
                disabled={!isValid || disabled}
                css={{ marginTop: 32, marginBottom: 160 }}
                onClick={onSubmit}>
                {t("upload.metadata.save")}
            </Button>
        </MetadataForm>
    </FormProvider>;
};


// ==============================================================================================
// ===== Helper functions to send JWT-authenticated requests to OC
// ==============================================================================================

/** Returns the full Opencast URL for the given path */
const ocUrl = (path: string): string => `${CONFIG.opencast.uploadNode}${path}`;

/** Opencast returned a non-OK status code */
export class OcServerError extends Error {
    public status: number;
    public statusText: string;

    public constructor(status: number, statusText: string) {
        super();
        this.name = "Opencast server error";
        this.status = status;
        this.statusText = statusText;
        this.message = `OC returned non-2xx status ${status} ${statusText}`;
    }
}

/** Opencast returned a non-OK status code */
export class OcUnexpectedResponse extends Error {
    public constructor(msg: string) {
        super();
        this.name = "Opencast unexpected response";
        this.message = msg;
    }
}

/** Opencast could not be reached */
export class OcNetworkError extends Error {
    public inner?: Error;

    public constructor(inner?: Error) {
        super();
        this.name = "Opencast Network Error";
        this.inner = inner;
        this.message = `network error while contacting Opencast API: ${inner}`;
    }
}

/** The JWT sent to Opencast was rejected. */
export class JwtInvalid extends Error {
    public constructor() {
        super();
        this.name = "Opencast JWT Auth Error";
        this.message = "JWT was rejected by Opencast";
    }
}

/** Performs a request against Opencast, authenticated via JWT */
const ocRequest = async (
    path: string,
    options: RequestInit = {},
    id?: string,
): Promise<string> => {
    const jwt = await getJwt("UPLOAD", { opencastId: id });

    const url = ocUrl(path);
    const response = await fetch(url, {
        redirect: "manual",
        ...options,
        headers: {
            ...options.headers,
            "Authorization": `Bearer ${jwt}`,
        },
    }).catch(e => { throw new OcNetworkError(e); });

    // There should be no reason for a redirect except non-authenticated. The
    // underlying problem can hopefully be resolved soon, so that we can remove
    // this code.
    if (response.type === "opaqueredirect") {
        throw new JwtInvalid();
    }

    if (!response.ok) {
        throw new OcServerError(response.status, response.statusText);
    }


    return response.text()
        .catch(e => { throw new OcNetworkError(e); });
};

type InfoMeResponse = {
    roles: string[];
    userRole: string;
    user: {
        username: string;
        name?: string;
    };
};

const requestInfoMe = async (): Promise<InfoMeResponse> => {
    const err = () => new OcUnexpectedResponse("unexpected JSON from /info/me.json");

    const json = JSON.parse(await ocRequest("/info/me.json")) as unknown;
    if (!json || typeof json !== "object") {
        throw err();
    }
    if ("roles" in json && "userRole" in json && "user" in json
        && Array.isArray(json.roles) && json.roles.every(role => typeof role === "string")
        && typeof json.userRole === "string"
        && json.user && typeof json.user === "object"
        && "username" in json.user && typeof json.user.username === "string"
    ) {
        let name: string | undefined = undefined;
        if ("name" in json.user) {
            if (!(typeof json.user.name === "string")) {
                throw err();
            }
            name = json.user.name;
        }

        return {
            roles: json.roles,
            userRole: json.userRole,
            user: {
                username: json.user.username,
                ...name && { name },
            },
        };
    }

    throw err();
};

const isAnonymous = (user: InfoMeResponse) => (
    // If there is only ROLE_ANONYMOUS in the roles, it means we have no
    // user session, meaning the JWT is invalid.
    new Set([COMMON_ROLES.ANONYMOUS, ...user.roles]).size <= 1
);

const checkInfoMe = async () => {
    // Log Opencast user information. This is also a good test whether the
    // communication with OC works at all, without potentially creating an
    // empty new media package.
    const userInfo = await requestInfoMe();
    // eslint-disable-next-line no-console
    console.debug("JWT user: ", userInfo);

    if (isAnonymous(userInfo)) {
        // eslint-disable-next-line no-console
        console.error("'info/me.json' returned ANONYMOUS -> JWT not working");
        throw new JwtInvalid();
    }
};


// ==============================================================================================
// ===== Functions to perform actions against the ingest API
// ==============================================================================================

const startUpload = async (
    files: FileList,
    setUploadState: (state: UploadState) => void,
    onProgress: (progress: Progress) => void,
    onDone: (mediaPackage: string) => void,
    abortController: MutableRefObject<AbortController>,
) => {
    try {
        setUploadState({ state: "starting" });

        await checkInfoMe();

        // Create a new media package to start the upload
        let mediaPackage = await ocRequest("/ingest/createMediaPackage");

        const tracks = Array.from(files)
            .map(file => ({ file, flavor: "presentation/source" as const }));
        mediaPackage = await uploadTracks(
            mediaPackage,
            tracks,
            onProgress,
            abortController,
            setUploadState,
        );
        onDone(mediaPackage);
    } catch (error) {
        setUploadState({ state: "error", error });
    }
};


type Track = {
    flavor: "presentation/source" | "presenter/source";
    file: File;
};

/**
 * Uploads the given tracks via the ingest API. Calls `onProgress` regularly
 * with a number between 0 and 1, indicating how much of the data was already
 * uploaded. Returns the resulting media package returned by the last request.
 */
const uploadTracks = async (
    mediaPackage: string,
    tracks: Track[],
    onProgress: (progress: number) => void,
    abortController: MutableRefObject<AbortController>,
    setUploadState: (state: UploadState) => void,
): Promise<string> => {
    const totalBytes = tracks.map(t => t.file.size).reduce((a, b) => a + b, 0);
    let sizeFinishedTracks = 0;

    for (const { flavor, file } of tracks) {
        // Assemble multipart body
        const body = new FormData();
        body.append("mediaPackage", mediaPackage);
        body.append("flavor", flavor);
        body.append("tags", "");
        body.append("BODY", file, file.name);

        const url = ocUrl("/ingest/addTrack");
        const jwt = await getJwt("UPLOAD");
        mediaPackage = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            abortController.current.signal.addEventListener("abort", () => {
                xhr.abort();
                abortController.current = new AbortController();
                cancelUpload(mediaPackage, setUploadState);
            });

            xhr.open("POST", url);
            xhr.setRequestHeader("Authorization", `Bearer ${jwt}`);

            xhr.onload = () => {
                if (xhr.responseURL !== url) {
                    reject(new JwtInvalid());
                } else if (xhr.status !== 200) {
                    reject(new OcServerError(xhr.status, xhr.statusText));
                } else {
                    resolve(xhr.responseText);
                }
            };
            xhr.onerror = () => reject(new OcNetworkError());
            xhr.upload.onprogress = e => {
                const uploadedBytes = e.loaded + sizeFinishedTracks;
                onProgress(uploadedBytes / totalBytes);
            };

            try {
                xhr.send(body);
            } catch (e) {
                reject(e);
            }
        });

        sizeFinishedTracks += file.size;
    }

    return mediaPackage;
};


const cancelUpload = async (
    mediaPackage: string,
    setUploadState: (state: UploadState) => void,
) => {
    try {
        setUploadState({ state: "cancelled" });

        const body = new FormData();
        body.append("mediaPackage", mediaPackage);
        await ocRequest("/ingest/discardMediaPackage", { method: "post", body: body });
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error cancelling: ", error);
    }
};

/** Ingest the given metadata and finishes the ingest process */
const finishUpload = async (
    mediaPackage: string,
    metadata: Metadata,
    user: User,
    setUploadState: (state: UploadState) => void,
    createPlaceholder: (event: NewEvent) => Promise<Disposable>,
) => {
    try {
        setUploadState({ state: "finishing" });
        const mp = new DOMParser()
            .parseFromString(mediaPackage, "application/xml")
            .querySelector("mediapackage");

        const id = mp?.getAttribute("id") ?? "";

        // Add metadata in DC-Catalog
        {
            const dcc = constructDcc(metadata, user);
            const body = new URLSearchParams();
            body.append("mediaPackage", mediaPackage);
            body.append("dublinCore", dcc);
            body.append("flavor", "dublincore/episode");

            mediaPackage = await ocRequest(
                "/ingest/addDCCatalog",
                { method: "post", body },
                id,
            );
        }

        // Add ACL
        if (!CONFIG.lockAclToSeries || !metadata.series) {
            const acl = constructAcl(metadata.acl);
            const body = new FormData();
            body.append("flavor", "security/xacml+episode");
            body.append("mediaPackage", mediaPackage);
            body.append("BODY", new Blob([acl]), "acl.xml");

            mediaPackage = await ocRequest(
                "/ingest/addAttachment",
                { method: "post", body },
                id,
            );
        }

        // Add thumbnail attachment if provided
        if (metadata.thumbnail) {
            const body = new FormData();
            body.append("flavor", `presentation/${CONFIG.upload.thumbnailSubtype}`);
            body.append("mediaPackage", mediaPackage);
            body.append("BODY", metadata.thumbnail, metadata.thumbnail.name);

            mediaPackage = await ocRequest(
                "/ingest/addAttachment",
                { method: "post", body },
                id,
            );
        }

        // Finish ingest
        {
            const body = new FormData();
            body.append("mediaPackage", mediaPackage);
            if (CONFIG.upload.workflow) {
                body.append("workflowDefinitionId", CONFIG.upload.workflow);
            }

            if (metadata.thumbnail) {
                // We piggyback on the editor condition. Doing so allows the thumbnail
                // upload to work without any workflow changes or additions in Opencast itself.
                body.append(`presentation/${CONFIG.upload.thumbnailWorkflowProperty}`, "true");
            }

            await ocRequest("/ingest/ingest", { method: "post", body: body }, id);
        }

        // Create placeholder to show on 'my videos' until the event is synced.
        {
            const ocId = new DOMParser()
                .parseFromString(mediaPackage, "text/xml")
                .documentElement
                .getAttribute("id");

            if (ocId) {
                createPlaceholder({
                    opencastId: ocId,
                    title: metadata.title,
                    description: metadata.description,
                    seriesId: metadata.series?.id,
                    acl: aclMapToArray(metadata.acl),
                    creators: [user.displayName],
                });
            }
        }

        setUploadState({ state: "done" });
    } catch (error) {
        setUploadState({ state: "error", error });
    }
};

/**
 * Encodes a value for inclusion in XML sent to Opencast.
 */
const encodeValue = (value: string): string =>
    new XMLSerializer().serializeToString(new Text(value));

/** Creates a Dublin Core Catalog in XML format that describes the given metadata. */
const constructDcc = (metadata: Metadata, user: User): string => {
    const tag = (tag: string, value?: string): string =>
        value ? `<${tag}>${encodeValue(value)}</${tag}>` : "";

    return `<?xml version="1.0" encoding="UTF-8"?>
        <dublincore xmlns="http://www.opencastproject.org/xsd/1.0/dublincore/"
                    xmlns:dcterms="http://purl.org/dc/terms/"
                    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
            <dcterms:created xsi:type="dcterms:W3CDTF">
                ${new Date().toISOString()}
            </dcterms:created>
            ${tag("dcterms:title", metadata.title)}
            ${tag("dcterms:description", metadata.description)}
            ${tag("dcterms:isPartOf", metadata.series?.ocId)}
            ${tag("dcterms:creator", user.displayName)}
            ${tag("dcterms:spatial", "Tobira Upload")}
        </dublincore>
    `;
};

/** Constructs an ACL XML description from the given roles that are allowd to read/write */
const constructAcl = (acl: Acl): string => {
    // TODO: maybe we should escape the role somehow?
    const makeRule = (action: string, role: string): string => `
        <Rule RuleId="${action}_permit_for_${role}" Effect="Permit">
          <Target>
            <Actions>
              <Action>
                <ActionMatch MatchId="urn:oasis:names:tc:xacml:1.0:function:string-equal">
                  <AttributeValue
                    DataType="http://www.w3.org/2001/XMLSchema#string">${action}</AttributeValue>
                </ActionMatch>
              </Action>
            </Actions>
          </Target>
          <Condition>
            <Apply FunctionId="urn:oasis:names:tc:xacml:1.0:function:string-is-in">
              <AttributeValue
                DataType="http://www.w3.org/2001/XMLSchema#string">${role}</AttributeValue>
            </Apply>
          </Condition>
        </Rule>
    `;

    const rules = [...acl.entries()]
        .flatMap(([role, info]) => [...info.actions].map(action => makeRule(action, role)));

    return `
        <?xml version="1.0" encoding="UTF-8" standalone="yes"?>
        <Policy PolicyId="mediapackage-1"
          RuleCombiningAlgId=
            "urn:oasis:names:tc:xacml:1.0:rule-combining-algorithm:permit-overrides"
          Version="2.0"
          xmlns="urn:oasis:names:tc:xacml:2.0:policy:schema:os">
            ${rules.join("\n")}
        </Policy>
    `.trim();
};
